<template>
  <generic-modal
    :title="$t('dpi verification')"
    ref="generic_modal"
    :is_closable="false"
    :is_wide="!isMobile"
    :show_footer="false"
  >
    <div class="box">
      <!-- Mensaje informativo -->
      <div class="notification is-info is-dark mb-0">
        <div class="media">
          <div class="media-left">
            <span class="icon has-text-info">
              <i class="fa fa-id-card"></i>
            </span>
          </div>
          <div class="media-content">
            <p>{{ $t("please verify your identity providing your dpi") }}</p>
          </div>
        </div>
      </div>

      <!-- Sección para ingresar código -->
      <div class="code-input-section">
        <div class="field">
          <label class="label">{{ $t("dpi") }}</label>
          <div class="control">
            <!-- Input mejorado para código -->
            <div
              class="code-input-container"
              :class="{ 'mobile-layout': isMobile }"
              v-if="!isMobile"
            >
              <input
                v-for="(digit, index) in 13"
                :key="index"
                :ref="`codeInput${index}`"
                class="code-digit-input"
                :class="{ 'mobile-input': isMobile }"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="1"
                :value="getDigit(index)"
                @input="onDigitInput(index, $event)"
                @keydown="onKeyDown(index, $event)"
                @paste="onPaste($event)"
                @focus="onInputFocus(index)"
                :placeholder="isMobile ? '' : '·'"
              />
            </div>

            <!-- Alternativa para móvil: input único con formato visual -->
            <div v-if="isMobile" class="mobile-dpi-input">
              <input
                ref="mobileInput"
                class="input is-large has-text-centered"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="13"
                v-model="meta.code"
                @input="onMobileInput"
                @keyup="formatMobileDisplay"
                :placeholder="$t('enter 13 digit dpi')"
                style="
                  font-family: 'Courier New', monospace;
                  /* letter-spacing: 0.2em; */
                "
              />
              <div class="mobile-format-helper">
                {{ formattedDpiDisplay }}
              </div>
            </div>
          </div>
          <p v-if="error" class="help is-danger">
            <i class="fa fa-exclamation-triangle"></i>
            {{ error }}
          </p>
          <p v-else class="help">
            {{ $t("enter the 13-digit dpi") }}
          </p>
          <!-- Indicador de progreso -->
          <div v-if="meta.code.length > 0" class="progress-indicator">
            <div class="progress-text">
              {{ meta.code.length }}/13 {{ $t("digits entered") }}
            </div>
            <progress
              class="progress is-small is-primary"
              :value="meta.code.length"
              max="13"
            >
              {{ Math.round((meta.code.length / 13) * 100) }}%
            </progress>
          </div>
        </div>

        <!-- Toggle para cambiar entre modo individual y móvil -->
        <!-- <div v-if="isMobile" class="field has-text-centered mb-4">
          <div class="buttons is-centered">
            <button
              class="button is-small"
              :class="{
                'is-primary': !showMobileAlternative,
                'is-light': showMobileAlternative,
              }"
              @click="showMobileAlternative = false"
            >
              <span class="icon is-small">
                <i class="fa fa-grip-horizontal"></i>
              </span>
              <span>{{ $t("individual digits") }}</span>
            </button>
            <button
              class="button is-small"
              :class="{
                'is-primary': showMobileAlternative,
                'is-light': !showMobileAlternative,
              }"
              @click="toggleMobileMode"
            >
              <span class="icon is-small">
                <i class="fa fa-keyboard-o"></i>
              </span>
              <span>{{ $t("single input") }}</span>
            </button>
          </div>
        </div> -->

        <div class="field">
          <div class="control">
            <button
              class="button is-success is-fullwidth"
              :class="[
                isMobile ? 'is-large' : 'is-medium',
                { 'is-loading': meta.verifying_code },
              ]"
              @click="verify_code"
              :disabled="meta.code.length !== 13 || meta.verifying_code"
              style="height: 3.5rem; font-size: 1.1rem"
            >
              <span class="icon">
                <i class="fa fa-check-circle"></i>
              </span>
              <span>{{ $t("verify dpi") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <!-- <template v-slot:footer>
      <button class="button" @click="close_modal">
        <span class="icon">
          <i class="fa fa-times"></i>
        </span>
        <span>{{ $t("cancel") }}</span>
      </button>
    </template> -->
  </generic-modal>
</template>

<script>
import GenericModal from "@/components/generic_modal/Index.vue";
import {
  post,
  put,
  get,
  success_message,
  generate_error_message_text,
  error_message,
} from "@/utils";
import moment from "moment";

export default {
  name: "DPIVerificationModal",
  components: { GenericModal },
  mounted() {
    // Agregar listener para cambios de orientación y redimensionado
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("orientationchange", this.handleOrientationChange);
  },
  beforeDestroy() {
    // Limpiar listeners
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener(
      "orientationchange",
      this.handleOrientationChange
    );
  },
  data() {
    return {
      account_request: null,
      error: "",
      showMobileAlternative: false,
      meta: {
        code: "",
        code_sent: false,
        sending_code: false,
        verifying_code: false,
        show_code_input: false,
      },
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth <= 768;
    },
    formattedDpiDisplay() {
      if (!this.meta.code) return "";
      // Formatear como: XXXX XXXX XXXXX
      const code = this.meta.code.padEnd(13, "·");
      return `${code.slice(0, 4)} ${code.slice(4, 8)} ${code.slice(8, 13)}`;
    },
  },
  methods: {
    show(account_request) {
      let v = this;
      v.account_request = account_request;
      // Detectar si es móvil y establecer el modo por defecto
      v.showMobileAlternative = v.isMobile;
      v.$refs.generic_modal.show_modal();
    },
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    set_initial_state() {
      this.meta = {
        code: "",
        code_sent: false,
        sending_code: false,
        verifying_code: false,
        show_code_input: false,
      };
      this.account_request = null;
      this.error = "";
      this.showMobileAlternative = false;
    },

    // Métodos para el input de código mejorado
    getDigit(index) {
      return this.meta.code[index] || "";
    },

    onDigitInput(index, event) {
      const value = event.target.value.replace(/[^0-9]/g, "");

      if (value.length > 0) {
        // Si se ingresaron múltiples dígitos (pegado), manejar diferente
        if (value.length > 1) {
          // Caso de pegado o entrada múltiple
          let newCode = this.meta.code.split("");
          for (let i = 0; i < value.length && index + i < 13; i++) {
            newCode[index + i] = value[i];
          }
          this.meta.code = newCode.join("").substring(0, 13);

          // Mover al siguiente input disponible
          const nextIndex = Math.min(index + value.length, 12);
          this.$nextTick(() => {
            const nextInput = this.$refs[`codeInput${nextIndex}`];
            if (nextInput && nextInput[0]) {
              nextInput[0].focus();
            }
          });
        } else {
          // Caso normal: un solo dígito
          let newCode = this.meta.code.split("");
          newCode[index] = value[value.length - 1]; // Solo el último dígito
          this.meta.code = newCode.join("").substring(0, 13);

          // Mover al siguiente input
          if (index < 12 && value.length > 0) {
            this.$nextTick(() => {
              const nextInput = this.$refs[`codeInput${index + 1}`];
              if (nextInput && nextInput[0]) {
                nextInput[0].focus();
              }
            });
          }
        }
      }

      this.clearError();
    },

    onKeyDown(index, event) {
      // Backspace: borrar y mover al anterior
      if (event.key === "Backspace") {
        if (this.getDigit(index) === "" && index > 0) {
          // Si está vacío, mover al anterior
          const prevInput = this.$refs[`codeInput${index - 1}`];
          if (prevInput && prevInput[0]) {
            prevInput[0].focus();
          }
        } else {
          // Borrar el dígito actual
          let newCode = this.meta.code.split("");
          newCode[index] = "";
          this.meta.code = newCode.join("").replace(/\s/g, "");
        }
      }

      // Arrow keys para navegación
      if (event.key === "ArrowLeft" && index > 0) {
        const prevInput = this.$refs[`codeInput${index - 1}`];
        if (prevInput && prevInput[0]) {
          prevInput[0].focus();
        }
      }

      if (event.key === "ArrowRight" && index < 12) {
        const nextInput = this.$refs[`codeInput${index + 1}`];
        if (nextInput && nextInput[0]) {
          nextInput[0].focus();
        }
      }
    },

    onPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData("text")
        .replace(/[^0-9]/g, "");

      if (pastedData.length > 0) {
        this.meta.code = pastedData.substring(0, 13);

        // Enfocar el último input con contenido o el primero vacío
        const focusIndex = Math.min(pastedData.length, 12);
        this.$nextTick(() => {
          const targetInput = this.$refs[`codeInput${focusIndex}`];
          if (targetInput && targetInput[0]) {
            targetInput[0].focus();
          }
        });
      }
    },

    clearError() {
      if (this.error && this.meta.code.length > 0) {
        this.error = "";
      }
    },

    // Método para enviar código SMS
    verify_code() {
      let v = this;

      if (!v.meta.code || v.meta.code.length !== 13) {
        v.error = v.$t("please enter a valid 13-digit dpi");
        return;
      }

      v.meta.verifying_code = true;
      v.error = "";

      const payload = {
        uuid: v.account_request.uuid,
        dpi: v.meta.code,
      };

      post("/account-request/personal-savings/complete/verify-dpi", payload)
        .then((/* response */) => {
          v.$emit("verification-completed", v.account_request);
          v.close_modal();
        })
        .catch((error) => {
          console.error("Error verifying DPI:", error);
          if (
            error.status === 500 &&
            error.data.includes("DPI does not match")
          ) {
            v.error = v.$t("the provided dpi does not match our records");
          } else if (
            error.status === 500 &&
            error.data.includes("invalid request id")
          ) {
            v.error = v.$t("invalid account request");
          } else {
            v.error =
              generate_error_message_text(error) || v.$t("error verifying dpi");
          }
          // error_message(error);
        })
        .finally(() => {
          v.meta.verifying_code = false;
        });
    },

    // Métodos para móvil
    toggleMobileMode() {
      this.showMobileAlternative = !this.showMobileAlternative;
      if (this.showMobileAlternative) {
        this.$nextTick(() => {
          const mobileInput = this.$refs.mobileInput;
          if (mobileInput) {
            mobileInput.focus();
          }
        });
      } else {
        this.$nextTick(() => {
          const firstInput = this.$refs.codeInput0;
          if (firstInput && firstInput[0]) {
            firstInput[0].focus();
          }
        });
      }
    },

    onMobileInput(event) {
      // Limpiar caracteres no numéricos
      let value = event.target.value.replace(/[^0-9]/g, "");
      if (value.length > 13) {
        value = value.substring(0, 13);
      }
      this.meta.code = value;
      this.clearError();
    },

    formatMobileDisplay() {
      // Este método se llama en keyup para actualizar la visualización
      // La lógica está en el computed formattedDpiDisplay
    },

    onInputFocus(index) {
      // Mejorar la experiencia en móvil
      if (this.isMobile) {
        // En móvil, seleccionar todo el contenido para facilitar el reemplazo
        const input = this.$refs[`codeInput${index}`];
        if (input && input[0]) {
          input[0].select();
        }
      }
    },

    // Manejar cambios de orientación y redimensionado
    handleResize() {
      // Forzar re-evaluación del computed isMobile
      this.$forceUpdate();
    },

    handleOrientationChange() {
      // Pequeño delay para que el navegador termine de cambiar la orientación
      setTimeout(() => {
        this.handleResize();
      }, 100);
    },

    // Validar input del código (simplificado)
    validateCodeInput() {
      // Este método ya no es necesario con el nuevo sistema
      // pero lo mantenemos por compatibilidad
      this.meta.code = this.meta.code.replace(/[^0-9]/g, "").substring(0, 13);
      this.clearError();
    },
  },
};
</script>

<style scoped>
.code-input-section {
  margin-top: 1.5rem;
}

.code-input-container {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
  flex-wrap: wrap;
}

.code-input-container.mobile-layout {
  gap: 0.4rem;
  padding: 0 0.5rem;
}

.code-digit-input {
  width: 3rem;
  height: 3rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: bold;
  border: 2px solid #dbdbdb;
  border-radius: 8px;
  background: white;
  transition: all 0.2s ease;
  font-family: "Courier New", monospace;
  /* Mejorar la experiencia táctil */
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.code-digit-input:focus {
  outline: none;
  border-color: #3273dc;
  box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
  transform: scale(1.05);
}

.code-digit-input:not(:placeholder-shown) {
  border-color: #48c774;
  background-color: #f0fdf4;
}

/* Estilos específicos para móvil */
.code-digit-input.mobile-input {
  width: 2.8rem;
  height: 2.8rem;
  font-size: 1.3rem;
  border-radius: 6px;
  /* Mejorar la experiencia táctil en móvil */
  touch-action: manipulation;
}

/* Input móvil alternativo */
.mobile-dpi-input {
  margin: 1rem 0;
  text-align: center;
}

.mobile-dpi-input .input {
  font-size: 1.3rem;
  font-weight: bold;
  border: 2px solid #dbdbdb;
  border-radius: 8px;
  transition: all 0.2s ease;
  /* Altura específica para móvil */
  height: 3.5rem;
}

.mobile-dpi-input .input:focus {
  border-color: #3273dc;
  box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
}

.mobile-format-helper {
  margin-top: 0.5rem;
  font-family: "Courier New", monospace;
  font-size: 1.1rem;
  color: #777;
  font-weight: bold;
  letter-spacing: 0.3em;
  min-height: 1.5rem;
}

/* Media queries para móviles */
@media screen and (max-width: 768px) {
  .code-input-section {
    margin-top: 1rem;
    padding: 0 0.5rem;
  }

  .code-input-container {
    gap: 0.3rem;
    margin: 0.5rem 0;
  }

  .code-digit-input {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1.2rem;
  }

  /* Ajustar para pantallas muy pequeñas */
  @media screen and (max-width: 480px) {
    .code-input-container {
      gap: 0.2rem;
    }

    .code-digit-input {
      width: 2.2rem;
      height: 2.2rem;
      font-size: 1.1rem;
    }
  }

  /* Mejorar los botones en móvil */
  .button.is-large {
    font-size: 1.2rem;
    height: 3.5rem;
  }

  /* Espaciado del modal en móvil */
  .box {
    margin: 0.5rem;
    padding: 1rem;
  }
}

/* Mejorar el mensaje informativo */
.notification.is-info.is-dark .media-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Botones de toggle responsivos */
.buttons.is-centered .button.is-small {
  font-size: 0.85rem;
  padding: 0.5rem 0.8rem;
}

/* Mejorar la accesibilidad táctil */
@media (hover: none) and (pointer: coarse) {
  .code-digit-input {
    /* Aumentar el área táctil en dispositivos touch */
    min-height: 44px;
    min-width: 44px;
  }

  .button {
    min-height: 44px;
  }
}

/* Animaciones suaves */
.code-digit-input,
.mobile-dpi-input .input,
.button {
  transition: all 0.2s ease-in-out;
}

/* Indicador de progreso */
.progress-indicator {
  margin: 1rem 0 0.5rem 0;
  text-align: center;
}

.progress-text {
  font-size: 0.9rem;
  color: #777;
  margin-bottom: 0.5rem;
}

.progress.is-small {
  height: 0.5rem;
  border-radius: 0.25rem;
}

@media screen and (max-width: 768px) {
  .progress-indicator {
    margin: 0.5rem 0;
  }

  .progress-text {
    font-size: 0.85rem;
  }
}

/* Ocultar spinner en inputs numéricos */
.code-digit-input::-webkit-outer-spin-button,
.code-digit-input::-webkit-inner-spin-button,
.mobile-dpi-input .input::-webkit-outer-spin-button,
.mobile-dpi-input .input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.code-digit-input[type="number"],
.mobile-dpi-input .input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
